// src/components/admin/AdminLekhiContent.js
import React, { useState } from 'react';
import {
  uploadLekhiContent,
  uploadLekhiLearnContent,
  deleteLekhiContent,
  getLekhiContentTotals,
  getLekhiContentBreakdown,
  LEKHI_TOPICS
} from '../../services/firebase/firestoreService';

const SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];
const LEVELS = ['easy', 'medium', 'hard'];

const CONTENT_TYPES = [
  { key: 'diagnostic', label: 'Diagnostic' },
  { key: 'practice_mcqs', label: 'Practice MCQs' },
  { key: 'practice_fillBlanks', label: 'Practice Fill-Blanks' },
  { key: 'practice_matchFollowing', label: 'Practice Match-Following' },
  { key: 'practice_flashcards', label: 'Practice Flashcards' },
  { key: 'test', label: 'Test' },
  { key: 'learn', label: 'Learn' }
];

export default function AdminLekhiContent() {
  // Upload state
  const [uploadType, setUploadType] = useState('diagnostic');
  const [testNumber, setTestNumber] = useState('');
  const [jsonText, setJsonText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Learn upload state
  const [learnSubject, setLearnSubject] = useState('grammar');
  const [learnTopic, setLearnTopic] = useState('jati');
  const [learnLevel, setLearnLevel] = useState('easy');
  const [learnContentType, setLearnContentType] = useState('text');
  const [learnText, setLearnText] = useState('');
  const [learnFile, setLearnFile] = useState(null);

  // Delete state
  const [deleteType, setDeleteType] = useState('diagnostic');
  const [delSubject, setDelSubject] = useState('grammar');
  const [delTopic, setDelTopic] = useState('');
  const [delLevel, setDelLevel] = useState('easy');
  const [delTestNumber, setDelTestNumber] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  // View state - top totals
  const [totals, setTotals] = useState(null);
  const [totalsLoading, setTotalsLoading] = useState(false);

  // View state - drill down
  const [drillType, setDrillType] = useState('diagnostic');
  const [drillSubject, setDrillSubject] = useState('grammar');
  const [drillTestNumber, setDrillTestNumber] = useState('');
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillResult, setDrillResult] = useState(null);
  const [drillError, setDrillError] = useState(null);
  const [expandedLearnCell, setExpandedLearnCell] = useState(null); // "topic_level"

  const formatCounts = (counts) =>
    Object.entries(counts).map(([k, v]) => `${k}: ${v}`).join(', ');

  const handleUpload = async () => {
    setUploadResult(null);

    if (uploadType === 'learn') {
      if (learnContentType === 'text' && !learnText.trim()) {
        setUploadResult({ success: false, error: 'Enter text content.' });
        return;
      }
      if (learnContentType === 'audio' && !learnFile) {
        setUploadResult({ success: false, error: 'Choose an audio file.' });
        return;
      }
      setUploading(true);
      try {
        const item = {
          subject: learnSubject,
          topic: learnTopic,
          level: learnLevel,
          type: learnContentType,
          ...(learnContentType === 'text' ? { text: learnText } : { file: learnFile })
        };
        const result = await uploadLekhiLearnContent(item);
        setUploadResult({ success: true, message: `Uploaded 1 ${result.type} item for ${learnSubject} / ${learnTopic} / ${learnLevel}.` });
        setLearnText('');
        setLearnFile(null);
        const fileInput = document.getElementById('learn-file-input');
        if (fileInput) fileInput.value = '';
      } catch (e) {
        setUploadResult({ success: false, error: e.message });
      } finally {
        setUploading(false);
      }
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      setUploadResult({ success: false, error: 'Invalid JSON. Check the pasted text.' });
      return;
    }
    if (uploadType === 'test' && !testNumber) {
      setUploadResult({ success: false, error: 'Enter a test number.' });
      return;
    }
    setUploading(true);
    try {
      const counts = await uploadLekhiContent(parsed, uploadType, testNumber);
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      setUploadResult({ success: true, message: `Uploaded ${total} items (${formatCounts(counts)}).` });
      setJsonText('');
      setTestNumber('');
    } catch (e) {
      setUploadResult({ success: false, error: e.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setDeleteResult(null);
    let opts = {};
    let label = '';
    if (deleteType === 'diagnostic') {
      if (!delTopic.trim()) { setDeleteResult({ success: false, error: 'Enter a topic.' }); return; }
      opts = { subject: delSubject, topic: delTopic.trim() };
      label = `all diagnostic questions for ${delSubject} / ${delTopic.trim()}`;
    } else if (deleteType === 'practice') {
      if (!delTopic.trim()) { setDeleteResult({ success: false, error: 'Enter a topic.' }); return; }
      opts = { subject: delSubject, topic: delTopic.trim(), level: delLevel };
      label = `all practice content (MCQs, fill-blanks, match, flashcards) for ${delSubject} / ${delTopic.trim()} / ${delLevel}`;
    } else if (deleteType === 'test') {
      if (!delTestNumber) { setDeleteResult({ success: false, error: 'Enter a test number.' }); return; }
      opts = { testNumber: delTestNumber, subject: delSubject, level: delLevel };
      label = `all test questions for Test ${delTestNumber} / ${delSubject} / ${delLevel}`;
    } else if (deleteType === 'learn') {
      if (!delTopic.trim()) { setDeleteResult({ success: false, error: 'Enter a topic.' }); return; }
      opts = { subject: delSubject, topic: delTopic.trim(), level: delLevel };
      label = `all Learn content (text + audio files) for ${delSubject} / ${delTopic.trim()} / ${delLevel}`;
    }
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const counts = await deleteLekhiContent(deleteType, opts);
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      setDeleteResult({ success: true, message: `Deleted ${total} items (${formatCounts(counts)}).` });
    } catch (e) {
      setDeleteResult({ success: false, error: e.message });
    } finally {
      setDeleting(false);
    }
  };

  const handleLoadTotals = async () => {
    setTotalsLoading(true);
    try {
      const result = await getLekhiContentTotals();
      setTotals(result);
    } catch (e) {
      setTotals(null);
    } finally {
      setTotalsLoading(false);
    }
  };

  const handleLoadDrillDown = async () => {
    setDrillError(null);
    setDrillResult(null);
    setExpandedLearnCell(null);
    if (drillType === 'test' && !drillTestNumber) {
      setDrillError('Enter a test number.');
      return;
    }
    setDrillLoading(true);
    try {
      const result = await getLekhiContentBreakdown({
        type: drillType,
        subject: drillSubject,
        testNumber: drillType === 'test' ? drillTestNumber : null
      });
      setDrillResult(result);
    } catch (e) {
      setDrillError(e.message);
    } finally {
      setDrillLoading(false);
    }
  };

  const handleLearnSubjectChange = (newSubject) => {
    setLearnSubject(newSubject);
    setLearnTopic(LEKHI_TOPICS[newSubject][0]);
    setUploadResult(null);
  };

  const getLearnDocsForCell = (topic, level) => {
    if (!drillResult?.docs) return [];
    return drillResult.docs.filter(d => d.topic === topic && d.level === level);
  };

  const showLevelColumns = drillType !== 'diagnostic';
  const isLearn = drillType === 'learn';

  return (
    <div>
      <div className="admin-section-title">Lekhi Content</div>

      {/* ===== UPLOAD ===== */}
      <div className="admin-card">
        <h3>Upload Content</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          Diagnostic &amp; Test: paste a JSON array. Practice: paste the JSON object with content-type keys. Learn: fill the form.
        </p>

        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Content type</label>
          <select value={uploadType}
            onChange={e => { setUploadType(e.target.value); setUploadResult(null); }}>
            <option value="diagnostic">Diagnostic</option>
            <option value="practice">Practice</option>
            <option value="test">Test</option>
            <option value="learn">Learn</option>
          </select>
        </div>

        {uploadType === 'test' && (
          <div className="admin-form-group" style={{ marginBottom: '12px' }}>
            <label>Test number</label>
            <input type="number" value={testNumber}
              onChange={e => { setTestNumber(e.target.value); setUploadResult(null); }}
              placeholder="e.g. 1" />
          </div>
        )}

        {uploadType !== 'learn' && (
          <div className="admin-form-group" style={{ marginBottom: '12px' }}>
            <label>Paste JSON</label>
            <textarea value={jsonText}
              onChange={e => { setJsonText(e.target.value); setUploadResult(null); }}
              style={{ minHeight: '160px', resize: 'vertical', fontFamily: 'monospace', fontSize: '12px' }}
              placeholder='[ { "subject": "grammar", ... } ]' />
          </div>
        )}

        {uploadType === 'learn' && (
          <>
            <div className="admin-form-group" style={{ marginBottom: '12px' }}>
              <label>Subject</label>
              <select value={learnSubject}
                onChange={e => handleLearnSubjectChange(e.target.value)}>
                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="admin-form-group" style={{ marginBottom: '12px' }}>
              <label>Topic</label>
              <select value={learnTopic}
                onChange={e => { setLearnTopic(e.target.value); setUploadResult(null); }}>
                {LEKHI_TOPICS[learnSubject].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="admin-form-group" style={{ marginBottom: '12px' }}>
              <label>Level</label>
              <select value={learnLevel}
                onChange={e => { setLearnLevel(e.target.value); setUploadResult(null); }}>
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>

            <div className="admin-form-group" style={{ marginBottom: '12px' }}>
              <label>Type</label>
              <select value={learnContentType}
                onChange={e => { setLearnContentType(e.target.value); setUploadResult(null); }}>
                <option value="text">Text</option>
                <option value="audio">Audio</option>
              </select>
            </div>

            {learnContentType === 'text' && (
              <div className="admin-form-group" style={{ marginBottom: '12px' }}>
                <label>Text content</label>
                <textarea value={learnText}
                  onChange={e => { setLearnText(e.target.value); setUploadResult(null); }}
                  style={{ minHeight: '160px', resize: 'vertical', fontSize: '14px' }}
                  placeholder="Paste the Marathi text here..." />
              </div>
            )}

            {learnContentType === 'audio' && (
              <div className="admin-form-group" style={{ marginBottom: '12px' }}>
                <label>Audio file (max 10 MB; mp3, m4a, mp4)</label>
                <input id="learn-file-input" type="file"
                  accept="audio/*,video/mp4"
                  onChange={e => { setLearnFile(e.target.files?.[0] || null); setUploadResult(null); }} />
                {learnFile && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                    {learnFile.name} — {(learnFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                )}
              </div>
            )}
          </>
        )}

        <button className="admin-btn admin-btn-primary"
          onClick={handleUpload}
          disabled={uploading || (uploadType !== 'learn' && !jsonText.trim()) || (uploadType === 'learn' && learnContentType === 'text' && !learnText.trim()) || (uploadType === 'learn' && learnContentType === 'audio' && !learnFile)}>
          {uploading ? 'Uploading...' : 'Upload'}
        </button>

        {uploadResult && (
          <div className={`admin-status-msg ${uploadResult.success ? 'success' : 'error'}`}>
            {uploadResult.success ? `✓ ${uploadResult.message}` : `Error: ${uploadResult.error}`}
          </div>
        )}
      </div>

      {/* ===== VIEW ===== */}
      <div className="admin-card">
        <h3>View Content</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          Overview shows total items per content type. Drill down to see topic × level breakdown for any type.
        </p>

        <button className="admin-btn admin-btn-secondary"
          onClick={handleLoadTotals} disabled={totalsLoading}>
          {totalsLoading ? 'Loading...' : totals ? 'Refresh Overview' : 'Load Overview'}
        </button>

        {totals && (
          <div style={{ marginTop: '16px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e9ecef', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>Content Type</th>
                  <th style={{ padding: '8px' }}>Total Items</th>
                </tr>
              </thead>
              <tbody>
                {CONTENT_TYPES.map(({ key, label }) => (
                  <tr key={key} style={{ borderBottom: '1px solid #f1f3f5' }}>
                    <td style={{ padding: '8px' }}>{label}</td>
                    <td style={{ padding: '8px' }}>{totals[key] ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: '24px', padding: '16px', background: '#f8f9fa', borderRadius: '8px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px' }}>Drill Down</h4>

              <div className="admin-form-group" style={{ marginBottom: '10px' }}>
                <label>Content type</label>
                <select value={drillType}
                  onChange={e => { setDrillType(e.target.value); setDrillResult(null); setDrillError(null); }}>
                  {CONTENT_TYPES.map(({ key, label }) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="admin-form-group" style={{ marginBottom: '10px' }}>
                <label>Subject</label>
                <select value={drillSubject}
                  onChange={e => { setDrillSubject(e.target.value); setDrillResult(null); }}>
                  {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {drillType === 'test' && (
                <div className="admin-form-group" style={{ marginBottom: '10px' }}>
                  <label>Test number</label>
                  <input type="number" value={drillTestNumber}
                    onChange={e => { setDrillTestNumber(e.target.value); setDrillResult(null); }}
                    placeholder="e.g. 1" />
                </div>
              )}

              <button className="admin-btn admin-btn-secondary"
                onClick={handleLoadDrillDown} disabled={drillLoading}>
                {drillLoading ? 'Loading...' : 'Show Breakdown'}
              </button>

              {drillError && (
                <div className="admin-status-msg error" style={{ marginTop: '10px' }}>
                  Error: {drillError}
                </div>
              )}

              {drillResult && (
                <div style={{ marginTop: '16px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left', background: '#fff' }}>
                        <th style={{ padding: '8px' }}>Topic</th>
                        {showLevelColumns ? (
                          LEVELS.map(l => <th key={l} style={{ padding: '8px', textTransform: 'capitalize' }}>{l}</th>)
                        ) : (
                          <th style={{ padding: '8px' }}>Count</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(drillResult.grid).map(([topic, levels]) => (
                        <React.Fragment key={topic}>
                          <tr style={{ borderBottom: '1px solid #f1f3f5', background: levels._invalid ? '#fff3cd' : '#fff' }}>
                            <td style={{ padding: '8px' }}>
                              {topic}
                              {levels._invalid && <span style={{ color: '#856404', fontSize: '11px', marginLeft: '6px' }}>(unexpected)</span>}
                            </td>
                            {showLevelColumns ? (
                              LEVELS.map(l => {
                                const count = levels[l] || 0;
                                const cellKey = `${topic}_${l}`;
                                const expanded = expandedLearnCell === cellKey;
                                return (
                                  <td key={l} style={{ padding: '8px' }}>
                                    {isLearn && count > 0 ? (
                                      <button
                                        onClick={() => setExpandedLearnCell(expanded ? null : cellKey)}
                                        style={{
                                          background: 'none', border: 'none', color: '#007fff',
                                          cursor: 'pointer', textDecoration: 'underline', padding: 0,
                                          fontSize: '13px'
                                        }}>
                                        {count}
                                      </button>
                                    ) : count}
                                  </td>
                                );
                              })
                            ) : (
                              <td style={{ padding: '8px' }}>{levels.medium || 0}</td>
                            )}
                          </tr>

                          {isLearn && showLevelColumns && LEVELS.map(l => {
                            const cellKey = `${topic}_${l}`;
                            if (expandedLearnCell !== cellKey) return null;
                            const docs = getLearnDocsForCell(topic, l);
                            return (
                              <tr key={`${cellKey}_expand`}>
                                <td colSpan={LEVELS.length + 1} style={{ padding: '8px 16px 12px 24px', background: '#fafbfc' }}>
                                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                                    {topic} / {l} — {docs.length} item(s):
                                  </div>
                                  {docs.map(d => (
                                    <div key={d.id} style={{ marginBottom: '6px', fontSize: '13px' }}>
                                      {d.type === 'text' ? (
                                        <span>📄 Text: {d.text ? d.text.substring(0, 80) + (d.text.length > 80 ? '...' : '') : '(empty)'}</span>
                                      ) : (
                                        <span>
                                          🔊 Audio: <a href={d.audioURL} target="_blank" rel="noopener noreferrer"
                                            style={{ color: '#007fff' }}>{d.fileName || 'open'}</a>
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ===== DELETE ===== */}
      <div className="admin-card">
        <h3>Delete Content</h3>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          Deletes a whole slice so you can re-upload corrected content. For Learn, audio files in Storage are also removed. Cannot be undone.
        </p>

        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Content type</label>
          <select value={deleteType}
            onChange={e => { setDeleteType(e.target.value); setDeleteResult(null); }}>
            <option value="diagnostic">Diagnostic (by subject + topic)</option>
            <option value="practice">Practice (by subject + topic + level)</option>
            <option value="test">Test (by test number + subject + level)</option>
            <option value="learn">Learn (by subject + topic + level)</option>
          </select>
        </div>

        <div className="admin-form-group" style={{ marginBottom: '12px' }}>
          <label>Subject</label>
          <select value={delSubject}
            onChange={e => { setDelSubject(e.target.value); setDeleteResult(null); }}>
            {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {deleteType === 'test' && (
          <div className="admin-form-group" style={{ marginBottom: '12px' }}>
            <label>Test number</label>
            <input type="number" value={delTestNumber}
              onChange={e => { setDelTestNumber(e.target.value); setDeleteResult(null); }}
              placeholder="e.g. 1" />
          </div>
        )}

        {(deleteType === 'diagnostic' || deleteType === 'practice' || deleteType === 'learn') && (
          <div className="admin-form-group" style={{ marginBottom: '12px' }}>
            <label>Topic (code, e.g. jati)</label>
            <input type="text" value={delTopic}
              onChange={e => { setDelTopic(e.target.value); setDeleteResult(null); }}
              placeholder="e.g. jati" />
          </div>
        )}

        {(deleteType === 'practice' || deleteType === 'test' || deleteType === 'learn') && (
          <div className="admin-form-group" style={{ marginBottom: '12px' }}>
            <label>Level</label>
            <select value={delLevel}
              onChange={e => { setDelLevel(e.target.value); setDeleteResult(null); }}>
              {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        )}

        <button className="admin-btn admin-btn-secondary"
          onClick={handleDelete}
          disabled={deleting}>
          {deleting ? 'Deleting...' : 'Delete Slice'}
        </button>

        {deleteResult && (
          <div className={`admin-status-msg ${deleteResult.success ? 'success' : 'error'}`}>
            {deleteResult.success ? `✓ ${deleteResult.message}` : `Error: ${deleteResult.error}`}
          </div>
        )}
      </div>
    </div>
  );
}